import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  createStyles,
  makeStyles,
  Theme,
  Grid,
} from '@material-ui/core';
import { Filter, FilterMap } from '../../types/main';
import { selectCurrentUser } from '../../store/modules/users/selectors';
import { empty } from '../../utility/filters';
import FilterChip from './FilterChip';
import { selectDuplicateProject } from '../../store/modules/projects/selectors';
import { makeFilterMap } from './utils';

const useStyles = makeStyles(({ palette, colors }: Theme) => createStyles({
  root: {
    display: 'flex',
    borderBottom: `1px solid ${colors.outlines}`,
    color: palette.filterChip.color,
    overflowX: 'auto',
  },
}), { name: 'FiltersBar' });

type Props = {
  available: string[];
  active: Filter[];
  onChange: (active: Filter[]) => void;
  filterMap?: FilterMap[];
  className?: string;
};

const FiltersBar = ({
  available,
  active,
  onChange,
  filterMap,
  className = '',
}: Props) => {
  const classes = useStyles();

  const { loading, error } = useSelector(selectDuplicateProject);
  const { data: user } = useSelector(selectCurrentUser);
  const supportedFilters = useMemo(() => (user ? makeFilterMap(user, filterMap) : []), [user, filterMap]);

  const availableFilters = useMemo(
    () => available
      .map((key) => supportedFilters.find((filter) => filter.key === key))
      .filter(empty),
    [supportedFilters, available],
  );

  const withActiveFilters = useMemo(() => {
    if (active.length > 1 && filterMap) {
      const withoutRequiredFilter = active.filter((item) => filterMap
        .find((item2) => item2.key === item.key)).length > 1;

      return availableFilters.map((filter) => ({
        ...filter,
        isActive: active.some(
          (activeFilter) => activeFilter.key === filter.key
            && activeFilter.value.toString() === filter.value.toString()
            && (withoutRequiredFilter ? !filter.required : true),
        ),
      }));
    }
    return availableFilters.map((filter) => ({
      ...filter,
      isActive: active.some(
        (activeFilter) => activeFilter.key === filter.key,
      ),
    }));
  },
  [availableFilters, active, filterMap]);

  const handleChange = useCallback((filter: Filter, state: boolean, multi: boolean) => {
    const multipleFilters = (filter.filters) ? filter.filters : [];
    if (multi) {
      if (state) {
        return onChange([...active, filter, ...multipleFilters]);
      }

      return onChange(
        active.filter((activeFilter) => activeFilter.key !== filter.key),
      );
    }

    return onChange([filter, ...multipleFilters]);
  },
  [active, onChange]);

  /* eslint react-hooks/exhaustive-deps: 0 */
  useEffect(() => {
    if (!loading && !error && user?.id) {
      handleChange({ key: 'user_owner_id', value: user?.id }, true, false);
    }
  }, [loading, error]);

  return (
    <>
      {withActiveFilters.length > 0 && (
        <Grid className={`${classes.root} ${className}`}>
          {withActiveFilters.map((filter) => (
            <FilterChip
              {...filter}
              objKey={filter.key}
              onChange={handleChange}
            />
          ))}
        </Grid>
      )}
    </>
  );
};

export default FiltersBar;
